import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CurrentUser } from '@td/shared';
import { get, post, ApiRequestError } from '../lib/api';

interface AuthValue {
  user: CurrentUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        return await get<CurrentUser>('/auth/me');
      } catch (err) {
        // 401 is the normal signed-out state, not an error worth retrying.
        if (err instanceof ApiRequestError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const value = useMemo<AuthValue>(
    () => ({
      user: data ?? null,
      loading: isLoading,
      async signIn(email, password) {
        const user = await post<CurrentUser>('/auth/login', { email, password });
        queryClient.setQueryData(['auth', 'me'], user);
      },
      async signOut() {
        await post('/auth/logout');
        queryClient.setQueryData(['auth', 'me'], null);
        queryClient.clear();
      },
    }),
    [data, isLoading, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
